import { FC, useState, useEffect, useCallback, useRef } from 'react';
import UI from './util';

import './style.scss';

const PREFIX = 'ui-mobile-load-more';

interface IProps {
  /** 加载数据函数，返回Promise */
  loadData: () => Promise<{
    data: any[];
  }>;
  /** 渲染数据列表函数，参数为数据列表 */
  render: (param: any[]) => any;
  /** 当前列表是否已经结束 */
  isEnd?: boolean;
  /** 占位符 */
  placeholder?: any;
  /** 结束时是否展示结束文案 */
  showEnd?: boolean;
  /** 初始列表 */
  initList?: any[];
  /** 容器类名 */
  className?: string;
  /** 结束文案，或者可以自定义dom结构 */
  endText?: any;
  /** 加载文案， 或者可以自定义dom结构 */
  loadingText?: any;
  /** 在未结束的情况下是否一直显示loading样式 */
  alwaysShowLoading?: boolean;
  /** 距离底部开始加载的距离 */
  distanceToLoad?: number;
}

const LoadMore: FC<IProps> = (props) => {
  const {
    render,
    loadData,
    className = '',
    initList = [],
    showEnd = true,
    loadingText = 'loading',
    endText = 'No More Results',
    placeholder = null,
    isEnd = false,
    alwaysShowLoading = false,
    distanceToLoad = 0,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [dataList, setDataList] = useState(initList);

  const listRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const listDom = render(dataList);

  const doLoadMore = useCallback(() => {
    setIsLoading(true);
    loadData()
      .then((res: { data: any[] }) => {
        const { data } = res;
        setDataList((list) => [...list, ...data]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [loadData]);

  const scrollLoadMore = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (isEnd) {
      return;
    }

    const { height, top } = UI.offset(listRef.current);

    timerRef.current = setTimeout(() => {
      if (!isLoading && height + top <= window.innerHeight + UI.scrollY() + (distanceToLoad || 0)) {
        doLoadMore();
      }
    }, 200);
  }, [isLoading, isEnd, doLoadMore]);

  useEffect(() => {
    document.addEventListener('touchmove', scrollLoadMore);
    document.addEventListener('scroll', scrollLoadMore, { passive: false, capture: true });

    return () => {
      document.addEventListener('touchmove', scrollLoadMore);
      document.addEventListener('scroll', scrollLoadMore, { passive: false, capture: true });
    };
  }, [scrollLoadMore]);

  useEffect(() => {
    scrollLoadMore(); // 默认触发一次，解决首屏不满一屏且没有load more的问题
  }, []);

  const renderBottomText = (): JSX.Element | null => {
    if (isLoading || (!isEnd && alwaysShowLoading)) {
      return <div className="end-text">{loadingText}</div>;
    }
    if (isEnd && showEnd) {
      return <div className="end-text">{endText}</div>;
    }
    return null;
  };

  const renderPlaceholder = (): JSX.Element | null => {
    if (isLoading || dataList.length > 0 || (!isEnd && alwaysShowLoading)) {
      return null;
    }
    return placeholder;
  };

  return (
    <div className={`${PREFIX}-container ${className}`}>
      <div ref={listRef} className={`${PREFIX}-list-wrapper`}>
        {listDom}
      </div>
      <div className={`${PREFIX}-bottom-wrapper`}>{renderBottomText()}</div>
      <div className={`${PREFIX}-placeholder`}>{renderPlaceholder()}</div>
    </div>
  );
};

export default LoadMore;
